import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';

/**
 * Token de contexto para OPTAR POR NO recibir el snack global de error (D-06).
 * Ponlo en las requests cuyo error ya se muestra de otra forma (un estado
 * in-page con "Reintentar", o un snack específico del componente), para que
 * el usuario no vea dos avisos del mismo fallo:
 *
 *   this.http.post(url, body, {
 *     context: new HttpContext().set(SKIP_ERROR_NOTIFY, true),
 *   })
 */
export const SKIP_ERROR_NOTIFY = new HttpContextToken<boolean>(() => false);

/**
 * Interceptor global de errores HTTP (D-06). Red de seguridad app-wide: ante un
 * 4xx/5xx (o un fallo de red) que el componente NO maneja explícitamente,
 * muestra un snack legible en vez de dejar la operación fallar en silencio.
 *
 * Se excluye:
 *  - 401 → lo maneja `authErrorInterceptor` (fuerza logout).
 *  - requests con `SKIP_ERROR_NOTIFY` en el contexto (el componente ya avisa).
 *
 * SIEMPRE re-lanza el error: los callbacks de error de los componentes siguen
 * corriendo (apagar spinners, setear estados in-page, etc.).
 */
let ultimoMensaje = '';
let ultimoInstante = 0;

export const errorNotifyInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const snack = inject(MatSnackBar);
  return next(req).pipe(
    catchError((err) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status !== 401 &&
        !req.context.get(SKIP_ERROR_NOTIFY)
      ) {
        const msg = mensajeDeError(err);
        // Dedup: varias requests que fallan a la vez (p.ej. dos GET en paralelo)
        // no deben apilar snacks idénticos.
        const ahora = Date.now();
        if (msg !== ultimoMensaje || ahora - ultimoInstante > 3000) {
          ultimoMensaje = msg;
          ultimoInstante = ahora;
          snack.open(msg, 'OK', { duration: 6000 });
        }
      }
      return throwError(() => err);
    }),
  );
};

/** Traduce un HttpErrorResponse a un mensaje humano en español. */
function mensajeDeError(err: HttpErrorResponse): string {
  // FastAPI manda el motivo en `detail` (string) o, en validaciones (422),
  // una lista de objetos {loc, msg, type}.
  const detail = (err.error as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length) {
    const primero = detail[0] as { msg?: string };
    if (primero?.msg) {
      // pydantic v2 antepone "Value error, " a los ValueError propios.
      return primero.msg.replace(/^Value error,\s*/i, '');
    }
  }
  switch (err.status) {
    case 0:
      return 'No se pudo conectar con el servidor. Revisa tu conexión.';
    case 403:
      return 'No tienes permiso para realizar esta acción.';
    case 404:
      return 'No se encontró el recurso solicitado.';
    case 409:
      return 'La operación no se pudo completar (conflicto con el estado actual).';
    default:
      return err.status >= 500
        ? 'Ocurrió un error en el servidor. Intenta de nuevo en un momento.'
        : 'No se pudo completar la operación.';
  }
}
